/**
 * Serviço de Cache Inteligente
 * Implementa cache em memória e localStorage para otimizar performance
 */

class CacheService {
  constructor() {
    this.memoryCache = new Map();
    this.cacheConfig = {
      // Tempos de expiração por tipo de dado (em milissegundos)
      students: 5 * 60 * 1000, // 5 minutos
      turmas: 10 * 60 * 1000,  // 10 minutos
      attendance: 2 * 60 * 1000, // 2 minutos
      dashboard: 3 * 60 * 1000,  // 3 minutos
      revistas: 15 * 60 * 1000,  // 15 minutos
      default: 5 * 60 * 1000     // 5 minutos padrão
    };
    
    this.maxMemoryItems = 50; // Máximo de itens na memória
  }

  /**
   * Gerar chave única para cache
   * IMPORTANTE: Inclui igreja_id para isolar dados por igreja
   */
  generateKey(type, params = {}) {
    // Obter igreja_id do localStorage para isolar cache por igreja
    const igrejaId = localStorage.getItem('ebd_igreja_id') || 'no_igreja';
    
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}:${params[key]}`)
      .join('|');
    
    // Incluir igreja_id na chave para isolamento
    return `${type}_igreja_${igrejaId}${paramString ? `_${paramString}` : ''}`;
  }

  /**
   * Obter dados do cache
   */
  get(type, params = {}, useMemoryOnly = false) {
    const key = this.generateKey(type, params);
    
    // Tentar cache em memória primeiro
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem && this.isValid(memoryItem)) {
      memoryItem.lastAccessed = Date.now();
      return memoryItem.data;
    }

    // Se não usar apenas memória, tentar localStorage
    if (!useMemoryOnly) {
      try {
        const localItem = localStorage.getItem(`cache_${key}`);
        if (localItem) {
          const parsed = JSON.parse(localItem);
          if (this.isValid(parsed)) {
            // Promover para memória
            this.setMemoryCache(key, parsed.data, parsed.expiry);
            return parsed.data;
          } else {
            // Remover item expirado
            localStorage.removeItem(`cache_${key}`);
          }
        }
      } catch (error) {
      }
    }

    return null;
  }

  /**
   * Salvar dados no cache
   */
  set(type, data, params = {}, customTTL = null) {
    const key = this.generateKey(type, params);
    const ttl = customTTL || this.cacheConfig[type] || this.cacheConfig.default;
    const expiry = Date.now() + ttl;

    // Salvar na memória
    this.setMemoryCache(key, data, expiry);

    // Salvar no localStorage (para persistência)
    try {
      const cacheItem = {
        data,
        expiry,
        timestamp: Date.now(),
        type
      };
      localStorage.setItem(`cache_${key}`, JSON.stringify(cacheItem));
    } catch (error) {
      // Se localStorage estiver cheio, limpar cache antigo
      this.cleanupLocalStorage();
    }
  }

  /**
   * Salvar no cache de memória
   */
  setMemoryCache(key, data, expiry) {
    // Limpar cache se estiver muito grande
    if (this.memoryCache.size >= this.maxMemoryItems) {
      this.cleanupMemoryCache();
    }

    this.memoryCache.set(key, {
      data,
      expiry,
      lastAccessed: Date.now(),
      timestamp: Date.now()
    });
  }

  /**
   * Verificar se item do cache ainda é válido
   */
  isValid(item) {
    return item && item.expiry > Date.now();
  }

  /**
   * Invalidar cache específico
   */
  invalidate(type, params = {}) {
    const key = this.generateKey(type, params);
    
    // Remover da memória
    this.memoryCache.delete(key);
    
    // Remover do localStorage
    try {
      localStorage.removeItem(`cache_${key}`);
    } catch (error) {
    }
  }

  /**
   * Invalidar todos os caches de um tipo
   */
  invalidateType(type) {
    // Limpar memória
    for (const [key, item] of this.memoryCache.entries()) {
      if (key.startsWith(type)) {
        this.memoryCache.delete(key);
      }
    }

    // Limpar localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(`cache_${type}`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
    }
  }

  /**
   * Limpar cache de memória (remover itens menos acessados)
   */
  cleanupMemoryCache() {
    // Converter para array e ordenar por último acesso
    const items = Array.from(this.memoryCache.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => a.lastAccessed - b.lastAccessed);

    // Remover 25% dos itens menos acessados
    const toRemove = Math.floor(items.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      this.memoryCache.delete(items[i].key);
    }

  }

  /**
   * Limpar localStorage (remover cache expirado)
   */
  cleanupLocalStorage() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith('cache_'));
      let removedCount = 0;

      cacheKeys.forEach(key => {
        try {
          const item = JSON.parse(localStorage.getItem(key));
          if (!this.isValid(item)) {
            localStorage.removeItem(key);
            removedCount++;
          }
        } catch (error) {
          // Remover item corrompido
          localStorage.removeItem(key);
          removedCount++;
        }
      });

    } catch (error) {
    }
  }

  /**
   * Obter estatísticas do cache
   */
  getStats() {
    const memorySize = this.memoryCache.size;
    const localStorageKeys = Object.keys(localStorage)
      .filter(key => key.startsWith('cache_')).length;

    return {
      memoryItems: memorySize,
      localStorageItems: localStorageKeys,
      memoryUtilization: (memorySize / this.maxMemoryItems * 100).toFixed(1) + '%'
    };
  }

  /**
   * Limpar todo o cache
   */
  clearAll() {
    // Limpar memória
    this.memoryCache.clear();

    // Limpar localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('cache_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
    }
  }

  /**
   * Limpar cache de uma igreja específica
   */
  clearByIgreja(igrejaId) {
    // Limpar memória
    for (const [key, item] of this.memoryCache.entries()) {
      if (key.includes(`_igreja_${igrejaId}`)) {
        this.memoryCache.delete(key);
      }
    }

    // Limpar localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('cache_') && key.includes(`_igreja_${igrejaId}`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
    }
  }

  /**
   * Limpar todo o cache (todas as igrejas) - útil ao fazer logout
   */
  clearAllIgrejas() {
    this.clearAll();
  }

  /**
   * Cache com fallback para função
   */
  async getOrFetch(type, fetchFunction, params = {}, customTTL = null) {
    // Tentar obter do cache primeiro
    const cached = this.get(type, params);
    if (cached !== null) {
      return cached;
    }

    // Se não está no cache, buscar dados
    try {
      const data = await fetchFunction();
      
      // Salvar no cache
      this.set(type, data, params, customTTL);
      
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Pré-carregar dados no cache
   */
  async preload(type, fetchFunction, params = {}, customTTL = null) {
    try {
      const data = await fetchFunction();
      this.set(type, data, params, customTTL);
      return data;
    } catch (error) {
    }
  }

  /**
   * Inicializar limpeza automática
   */
  startAutoCleanup() {
    // Limpeza a cada 15 minutos
    setInterval(() => {
      this.cleanupLocalStorage();
      if (this.memoryCache.size > this.maxMemoryItems * 0.8) {
        this.cleanupMemoryCache();
      }
    }, 15 * 60 * 1000);

  }
}

// Instância singleton
const cacheService = new CacheService();

// Iniciar limpeza automática
cacheService.startAutoCleanup();

export default cacheService;